import { useRef, useState } from "react";
import { Download, Printer, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";

const RollupPage = () => {
  const rollupRef = useRef(null);
  const [showGuides, setShowGuides] = useState(false);

  const services = [
    "Bureaux & Locaux professionnels",
    "Nettoyage industriel",
    "Immeubles & Copropriétés",
    "Nettoyage après travaux",
    "Vitres & Surfaces vitrées",
    "Désinfection spécialisée"
  ];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-20">
      {/* Controls */}
      <div className="fixed top-24 right-6 z-50 bg-white rounded-xl shadow-lg p-4 space-y-3 print:hidden">
        <h3 className="font-bold text-slate-800 text-sm">Contrôles</h3>
        <Button 
          onClick={() => setShowGuides(!showGuides)}
          variant="outline"
          size="sm"
          className="w-full"
        >
          {showGuides ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          {showGuides ? "Masquer" : "Guides"}
        </Button>
        <Button 
          onClick={handlePrint}
          className="w-full bg-[#1E3A8A] hover:bg-[#2563EB]"
          size="sm"
        >
          <Printer className="w-4 h-4 mr-2" />
          Imprimer
        </Button>
        <div className="text-xs text-slate-500 text-center pt-2 border-t">
          Format: 85 × 200 cm<br/>
          Résolution: 300 DPI
        </div>
      </div>

      {/* Instructions */}
      <div className="container mx-auto px-4 py-8 print:hidden">
        <div className="bg-white rounded-xl p-6 shadow-lg max-w-3xl mx-auto mb-8">
          <h1 className="text-2xl font-bold text-[#1E3A8A] mb-4">Roll-up Nettis Facility</h1>
          <p className="text-slate-600 mb-4">
            Aperçu du roll-up au format 85cm × 200cm. Pour obtenir une version haute résolution :
          </p>
          <ol className="list-decimal list-inside space-y-2 text-slate-700 mb-4">
            <li>Cliquez sur <strong>"Imprimer"</strong> dans les contrôles</li>
            <li>Sélectionnez <strong>"Enregistrer en PDF"</strong> comme destination</li>
            <li>Configurez le format personnalisé : <strong>850mm × 2000mm</strong></li>
            <li>Envoyez le PDF à votre imprimeur professionnel</li>
          </ol>
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            💡 Conseil : Pour une impression optimale, envoyez également les fichiers sources (logo) à votre imprimeur.
          </p>
        </div>
      </div>

      {/* Roll-up Preview Container */}
      <div className="flex justify-center pb-20 print:p-0">
        <div 
          ref={rollupRef}
          className="relative bg-white shadow-2xl print:shadow-none"
          style={{
            width: "425px",  /* 85cm scaled to screen */
            height: "1000px", /* 200cm scaled to screen */
            minHeight: "1000px",
            /* For print, use actual dimensions */
          }}
        >
          {/* Print-specific styles */}
          <style>
            {`
              @media print {
                @page {
                  size: 850mm 2000mm;
                  margin: 0;
                }
                body {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .print\\:hidden {
                  display: none !important;
                }
              }
            `}
          </style>

          {/* Background - Clean White Design */}
          <div className="absolute inset-0 bg-white">
            {/* Top decorative gradient */}
            <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#1E3A8A] to-[#2563EB]"></div>
            {/* Bottom decorative accent */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-white"></div>
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, #1E3A8A 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}></div>
          </div>

          {/* Safety Guides */}
          {showGuides && (
            <div className="absolute inset-0 pointer-events-none z-50">
              {/* Safe zone - 3cm from edges */}
              <div 
                className="absolute border-2 border-dashed border-red-500"
                style={{
                  top: "15px",
                  left: "15px",
                  right: "15px",
                  bottom: "15px",
                }}
              ></div>
              {/* Bleed zone indicator */}
              <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                Zone de sécurité
              </div>
            </div>
          )}

          {/* Content */}
          <div className="relative h-full flex flex-col z-10 p-6">
            
            {/* Top Section - Logo on blue background */}
            <div className="flex-shrink-0 pt-6 pb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_nettis-large-format/artifacts/yrqjs65m_Nettis%20transparent.png"
                alt="Nettis Facility"
                className="h-24 w-auto mx-auto drop-shadow-lg"
                style={{ filter: 'brightness(1.15)' }}
              />
            </div>

            {/* Decorative Line */}
            <div className="flex-shrink-0 flex items-center justify-center mb-6 mt-4">
              <div className="w-20 h-1.5 bg-[#10B981] rounded-full shadow-lg"></div>
            </div>

            {/* Main Slogan */}
            <div className="flex-shrink-0 text-center px-3">
              <h1 className="text-3xl font-extrabold text-[#1E3A8A] leading-tight mb-2">
                Garantit un environnement
              </h1>
              <h2 className="text-5xl font-black text-[#10B981] leading-tight">
                PROPRE
              </h2>
            </div>

            {/* Subtitle */}
            <div className="flex-shrink-0 text-center mt-5 mb-8 px-4">
              <p className="text-base text-slate-600 leading-relaxed">
                Votre partenaire de confiance pour des services de nettoyage professionnels en Belgique
              </p>
            </div>

            {/* Services Section */}
            <div className="flex-grow flex flex-col justify-center">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mx-1 shadow-sm">
                <h3 className="text-lg font-bold text-[#1E3A8A] text-center mb-5 uppercase tracking-wider">
                  Nos Services
                </h3>
                <div className="space-y-3">
                  {services.map((service, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3"
                    >
                      <div className="w-3 h-3 rounded-full bg-[#10B981] flex-shrink-0 shadow-sm"></div>
                      <span className="text-slate-700 text-sm font-medium">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="flex-shrink-0 mt-6">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-[#1E3A8A] rounded-xl p-3 shadow-lg">
                  <div className="text-2xl font-black text-white">15+</div>
                  <div className="text-xs text-blue-200 mt-1">Années d'expérience</div>
                </div>
                <div className="bg-[#10B981] rounded-xl p-3 shadow-lg">
                  <div className="text-2xl font-black text-white">200</div>
                  <div className="text-xs text-green-100 mt-1">Clients satisfaits</div>
                </div>
                <div className="bg-[#1E3A8A] rounded-xl p-3 shadow-lg">
                  <div className="text-2xl font-black text-white">24/7</div>
                  <div className="text-xs text-blue-200 mt-1">Disponibilité</div>
                </div>
              </div>
            </div>

            {/* Eco Badge */}
            <div className="flex-shrink-0 mt-5 flex justify-center">
              <div className="inline-flex items-center gap-2 bg-[#10B981]/15 border-2 border-[#10B981] rounded-full px-4 py-2">
                <svg className="w-5 h-5 text-[#10B981]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span className="text-[#10B981] font-bold text-sm">Entreprise Éco-responsable</span>
              </div>
            </div>

            {/* Bottom Section - Contact */}
            <div className="flex-shrink-0 mt-auto pt-6 pb-4">
              {/* Divider */}
              <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent mb-5"></div>
              
              {/* Website */}
              <div className="text-center">
                <div className="inline-block bg-[#1E3A8A] rounded-full px-6 py-2.5 shadow-lg">
                  <span className="text-white font-bold text-lg tracking-wide">www.nettis.be</span>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="flex justify-center items-center gap-4 mt-4 text-slate-600 text-sm font-medium">
                <span>📞 +32 2 880 66 50</span>
                <span className="text-slate-300">•</span>
                <span>✉️ info@nettis.be</span>
              </div>
              
              {/* Address */}
              <div className="text-center mt-2 text-slate-500 text-xs">
                Avenue Louise 65 boîte 11, 1050 Bruxelles
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Download Info */}
      <div className="container mx-auto px-4 pb-20 print:hidden">
        <div className="bg-[#1E3A8A] rounded-xl p-6 shadow-lg max-w-3xl mx-auto text-white">
          <h3 className="font-bold text-lg mb-3">📋 Spécifications pour l'imprimeur</h3>
          <ul className="space-y-2 text-blue-100 text-sm">
            <li>• <strong>Format final :</strong> 85 cm × 200 cm</li>
            <li>• <strong>Résolution recommandée :</strong> 150-300 DPI</li>
            <li>• <strong>Zone de sécurité :</strong> 3 cm de chaque côté</li>
            <li>• <strong>Fond perdu :</strong> 3 mm</li>
            <li>• <strong>Mode colorimétrique :</strong> CMJN (pour impression)</li>
            <li>• <strong>Couleurs principales :</strong> Bleu #1E3A8A, Vert #10B981</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RollupPage;
